import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWebAuth } from "@/lib/webAuth";
import webApi from "@/lib/webApi";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Coins, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  awaiting_proof: { label: "Esperando comprobante", cls: "text-amber-400 bg-amber-500/10 border-amber-500/30" },
  pending: { label: "En revisión", cls: "text-blue-400 bg-blue-500/10 border-blue-500/30" },
  approved: { label: "Aprobada", cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
  rejected: { label: "Rechazada", cls: "text-red-400 bg-red-500/10 border-red-500/30" },
};

export default function WebOrders() {
  const { user, loading } = useWebAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const load = async () => {
    setReloading(true);
    try {
      const r = await webApi.get("/orders");
      setOrders(r.data);
    } catch (e) {
      toast.error("Error cargando órdenes");
    } finally {
      setReloading(false);
    }
  };

  useEffect(() => {
    if (user) {
      load();
      const id = setInterval(load, 20000);
      return () => clearInterval(id);
    }
  }, [user]);

  if (loading || !user) return null;

  return (
    <div className="min-h-[100dvh] bg-zinc-950 text-zinc-100">
      <header
        className="h-14 bg-zinc-950/85 backdrop-blur-xl border-b border-zinc-900 flex items-center px-3 sm:px-6 gap-3 sticky top-0 z-10"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/chat")}
          data-testid="orders-back-btn"
          className="text-zinc-400 hover:text-zinc-100"
        >
          <ArrowLeft size={16} className="sm:mr-1" />
          <span className="hidden sm:inline">Chat</span>
        </Button>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          data-testid="orders-refresh"
          className="text-zinc-500"
        >
          <RefreshCw size={14} className={reloading ? "animate-spin" : ""} />
        </Button>
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-sm bg-zinc-900 border border-zinc-800">
          <Coins size={13} className="text-amber-400" />
          <span className="text-xs font-mono font-bold">{user.credits ?? user.credits_web ?? 0}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="overline mb-2">Historial</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mb-6">
          Mis órdenes
        </h1>

        {orders.length === 0 && (
          <div className="text-center py-16 text-zinc-500">
            <div className="text-sm mb-4">Aún no tienes órdenes.</div>
            <Button
              onClick={() => navigate("/comprar")}
              data-testid="orders-go-buy"
              className="bg-[#2AABEE] hover:bg-[#229ED9] text-white rounded-sm"
            >
              Comprar créditos
            </Button>
          </div>
        )}

        <div className="space-y-3">
          {orders.map((o) => {
            const s = STATUS[o.status] || { label: o.status, cls: "text-zinc-400 bg-zinc-800/50 border-zinc-700" };
            return (
              <div
                key={o.id}
                className="border border-zinc-800 bg-zinc-900/50 rounded-md p-4"
                data-testid={`order-row-${o.id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="font-bold text-sm">{o.item_name}</div>
                    <code className="text-[10px] text-zinc-600 font-mono">
                      {o.reference}
                    </code>
                  </div>
                  <span
                    className={`inline-block text-[10px] font-mono font-bold px-2 py-0.5 rounded-sm border ${s.cls}`}
                  >
                    {s.label.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <div className="text-zinc-500">
                    +{o.credits_or_days} créditos
                  </div>
                  <div className="text-zinc-300 font-mono">
                    ${o.price_usd} USD
                  </div>
                </div>
                <div className="text-[10px] text-zinc-600 mt-2">
                  {new Date(o.created_at).toLocaleString("es-MX")}
                </div>
                {o.status === "awaiting_proof" && (
                  <Button
                    size="sm"
                    onClick={() => navigate("/comprar")}
                    data-testid={`order-resume-${o.id}`}
                    className="w-full mt-3 bg-amber-500 hover:bg-amber-600 text-black rounded-sm"
                  >
                    Subir comprobante
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
